import { useEffect, useRef } from 'react'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { useLocale } from '../../i18n/context'
import { useSwipeNavBlock } from '../../hooks/useSwipeNavBlock'
import type { MistakeRecord } from '../../types'

interface MistakesReviewSheetProps {
  isOpen: boolean
  onClose: () => void
  mistakes: MistakeRecord[]
}

/**
 * Read-only bottom sheet listing this session's mistakes. Display-only —
 * no re-study, no FSRS/mastery writes, no session creation. Mirrors
 * DefinitionBottomSheet's interaction pattern (swipe-down / backdrop / Escape).
 */
export default function MistakesReviewSheet({ isOpen, onClose, mistakes }: MistakesReviewSheetProps) {
  const { tr } = useLocale()
  const shouldReduceMotion = useReducedMotion()
  const sheetRef = useRef<HTMLDivElement>(null)
  // ADR-106 P3: blocks while open.
  useSwipeNavBlock(isOpen, 'mistakes-review-sheet')

  useEffect(() => {
    if (!isOpen) return
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [isOpen, onClose])

  const duration = shouldReduceMotion ? 0 : 0.22

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            key="mistakes-sheet-backdrop"
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration }}
            style={{
              position: 'fixed', inset: 0,
              background: 'rgba(0, 0, 0, 0.28)',
              zIndex: 90,
            }}
          />

          <motion.div
            key="mistakes-sheet"
            ref={sheetRef}
            role="dialog"
            aria-label={tr.complete.mistakesTitle}
            drag="y"
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={0.3}
            onDragEnd={(_, info) => {
              if (info.offset.y > 80 || info.velocity.y > 500) onClose()
            }}
            onPointerDownCapture={(e) => {
              if (e.clientX <= 20) e.stopPropagation()
            }}
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'tween', ease: [0.32, 0.72, 0, 1], duration }}
            style={{
              position: 'fixed', left: 0, right: 0, bottom: 0,
              maxHeight: '75dvh',
              background: 'var(--bg-surface)',
              borderTopLeftRadius: 20, borderTopRightRadius: 20,
              boxShadow: 'var(--shadow-float)',
              borderTop: '1px solid var(--border)',
              zIndex: 91,
              display: 'flex', flexDirection: 'column',
              touchAction: 'pan-y',
              paddingBottom: 'env(safe-area-inset-bottom)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'center', padding: '8px 0 4px', flexShrink: 0 }}>
              <div style={{ width: 40, height: 4, borderRadius: 50, background: 'var(--border)' }} />
            </div>

            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '4px 20px 10px', flexShrink: 0,
            }}>
              <span style={{
                fontSize: '0.6875rem', fontWeight: 600, color: 'var(--text-secondary)',
                textTransform: 'uppercase', letterSpacing: '0.1em',
              }}>
                {tr.complete.mistakesTitle}
              </span>
              <button
                onClick={onClose}
                aria-label={tr.complete.mistakesClose}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: 'var(--text-secondary)', padding: 6, borderRadius: 8,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  minWidth: 32, minHeight: 32,
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <div
              onPointerDownCapture={(e) => e.stopPropagation()}
              style={{
                flex: 1, minHeight: 0,
                overflowY: 'auto',
                overscrollBehavior: 'contain',
                padding: '4px 20px 24px',
              }}
            >
              {mistakes.map((m, i) => (
                <div
                  key={m.cardId}
                  style={{
                    padding: '14px 0',
                    borderTop: i === 0 ? undefined : '1px solid var(--border)',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
                    <span style={{
                      fontSize: '1rem', fontWeight: 600, color: 'var(--text-primary)',
                      overflowWrap: 'break-word', wordBreak: 'break-word',
                    }}>
                      {m.term}
                    </span>
                    {m.formLabel && (
                      <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                        {m.formLabel}
                      </span>
                    )}
                  </div>

                  {/* Focal point of the entry: comparable in size to the term heading above.
                      Neutral (not red — red is reserved for in-the-moment errors). The wrong
                      answer uses --text-primary (not --text-secondary) so the strikethrough is
                      the sole "incorrect" signal, not low contrast stacked on top of it. */}
                  <div style={{
                    display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap',
                    marginBottom: (m.definition || m.translation) ? 6 : 0,
                  }}>
                    {m.userAnswer && (
                      <span style={{
                        fontSize: '1rem', color: 'var(--text-primary)',
                        textDecoration: 'line-through',
                        overflowWrap: 'break-word', wordBreak: 'break-word',
                      }}>
                        {m.userAnswer}
                      </span>
                    )}
                    <span style={{ fontSize: '1rem', color: 'var(--text-secondary)' }}>→</span>
                    <span style={{
                      fontSize: '1rem', fontWeight: 700, color: 'var(--success)',
                      overflowWrap: 'break-word', wordBreak: 'break-word',
                    }}>
                      {m.correctAnswer}
                    </span>
                  </div>

                  {(m.translation || m.definition) && (
                    <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                      {m.translation ?? m.definition}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}
